"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VirResizableImage = void 0;
const common_1 = require("@augment-vir/common");
const element_vir_1 = require("element-vir");
const class_map_js_1 = require("lit/directives/class-map.js");
const dimensions_1 = require("./augments/dimensions");
const handle_iframe_1 = require("./handle-iframe");
const image_data_1 = require("./image-data");
const resizable_image_frame_1 = require("./resizable-image-frame");
const resizable_image_state_1 = require("./resizable-image-state");
const defaultClickCover = (0, element_vir_1.html) `
    <div class="click-cover"></div>
`;
exports.VirResizableImage = (0, element_vir_1.defineElement)()({
    tagName: 'vir-resizable-image',
    stateInit: resizable_image_state_1.defaultResizableImageState,
    hostClasses: {
        verticallyCenter: ({ state }) => state.shouldVerticallyCenter,
    },
    events: {
        settled: (0, element_vir_1.defineElementEvent)(),
        errored: (0, element_vir_1.defineElementEvent)(),
    },
    styles: ({ hostClassSelectors }) => (0, element_vir_1.css) `
        :host {
            position: relative;
            box-sizing: content-box;
            display: flex;
            justify-content: center;
            background-color: white;
            overflow: hidden;
        }

        ${hostClassSelectors.verticallyCenter} {
            align-items: center;
        }

        .click-cover {
            position: absolute;
            height: 100%;
            width: 100%;
            top: 0;
            left: 0;
            z-index: 100;
        }

        * {
            flex-shrink: 0;
        }

        .frame-constraint {
            position: relative;
            transition: 100ms;
            z-index: 100;
        }

        .error-wrapper,
        .loading-wrapper {
            min-width: 100%;
            min-height: 100%;
            max-width: 100%;
            max-height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            word-break: break-word;
        }

        .loading-wrapper {
            position: absolute;
            z-index: 200;
            background-color: inherit;
            opacity: 1;
            pointer-events: none;
        }

        .hide-loading-wrapper {
            /**
             * Only place the transition on the hide class so that when the loading wrapper should
             * show up, it shows up instantly.
             */
            transition: 500ms;
            opacity: 0;
        }

        iframe {
            display: block;
            border: none;
            max-width: calc(100% + 1px);
            max-height: calc(100% + 1px);
            width: calc(100% + 1px);
            height: calc(100% + 1px);
        }
    `,
    renderCallback: ({ state, inputs, updateState, host, dispatch, events }) => {
        function setSettled(value) {
            updateState({ settled: value });
            dispatch(new events.settled(value));
        }
        updateState({
            imageData: {
                createPromise: async () => {
                    setSettled(false);
                    updateState({
                        shouldVerticallyCenter: false,
                    });
                    try {
                        return (0, image_data_1.getImageData)(inputs.imageUrl, !!inputs.blockAutoPlay);
                    }
                    catch (error) {
                        // try again
                        await (0, common_1.wait)(1000);
                        try {
                            return (0, image_data_1.getImageData)(inputs.imageUrl, !!inputs.blockAutoPlay);
                        }
                        catch (error) {
                            dispatch(new events.errored((0, common_1.ensureError)(error)));
                            throw error;
                        }
                    }
                },
                trigger: {
                    ...(0, common_1.filterObject)(inputs, (key) => key !== 'extraHtml'),
                },
            },
        });
        const minConstraint = inputs.min && inputs.max
            ? (0, dimensions_1.clampDimensions)({ box: inputs.min, max: inputs.max })
            : inputs.min;
        const maxConstraint = inputs.max;
        const clampedForcedOriginalImageSize = inputs.forcedOriginalImageSize
            ? (0, dimensions_1.scaleToConstraints)({
                min: minConstraint,
                max: maxConstraint,
                box: inputs.forcedOriginalImageSize,
            })
            : undefined;
        const iframeTemplate = (0, element_vir_1.renderAsyncState)(state.imageData, '', (resolvedImageData) => {
            var _a;
            const previousFrameHtml = ((_a = state.frameFullHtml) === null || _a === void 0 ? void 0 : _a.imageUrl) === resolvedImageData.imageUrl
                ? state.frameFullHtml.html
                : undefined;
            return (0, element_vir_1.html) `
                    <iframe
                        loading="lazy"
                        referrerpolicy="no-referrer"
                        scrolling="no"
                        srcdoc=${previousFrameHtml ||
                (0, resizable_image_frame_1.generateIframeDoc)(resolvedImageData, inputs.extraHtml, inputs.htmlSizeQuerySelector)}
                        ${(0, element_vir_1.onDomCreated)(async () => {
                try {
                    const latestFrameSource = await (0, handle_iframe_1.handleIframe)({
                        updateState,
                        min: minConstraint,
                        max: maxConstraint,
                        host,
                        imageData: resolvedImageData,
                        forcedFinalImageSize: inputs.forcedFinalImageSize,
                        forcedOriginalImageSize: clampedForcedOriginalImageSize,
                    });
                    setSettled(true);
                    updateState({
                        frameFullHtml: {
                            imageUrl: resolvedImageData.imageUrl,
                            html: latestFrameSource,
                        },
                    });
                }
                catch (error) {
                    console.error(error);
                }
            })}
                    ></iframe>
                    <slot name="loaded"></slot>
                `;
        }, (error) => {
            dispatch(new events.errored((0, common_1.ensureError)(error)));
            return (0, element_vir_1.html) `
                    <div class="error-wrapper">
                        <slot name="error">${(0, common_1.extractErrorMessage)(error)}</slot>
                    </div>
                `;
        });
        const clickCoverTemplate = (0, element_vir_1.renderAsyncState)(state.imageData, defaultClickCover, (resolvedImageData) => {
            if ([
                image_data_1.ImageType.Html,
                image_data_1.ImageType.Video,
            ].includes(resolvedImageData.imageType)) {
                /**
                 * In this case the "image" is likely meant to be interactive, so don't block
                 * mouse interactions.
                 */
                return '';
            }
            else {
                return defaultClickCover;
            }
        }, () => '');
        const frameConstraintStyles = state.imageData instanceof Error
            ? (0, element_vir_1.css) `
                      max-width: 100%;
                      max-height: 100%;
                  `
            : clampedForcedOriginalImageSize
                ? (0, element_vir_1.css) `
                      width: ${clampedForcedOriginalImageSize.width}px;
                      height: ${clampedForcedOriginalImageSize.height}px;
                  `
                : '';
        return (0, element_vir_1.html) `
            <div
                class=${(0, class_map_js_1.classMap)({
            'loading-wrapper': true,
            'hide-loading-wrapper': state.settled,
        })}
            >
                <slot name="loading">Loading...</slot>
            </div>
            <div class="frame-constraint" style=${frameConstraintStyles}>${iframeTemplate}</div>
            ${clickCoverTemplate}
        `;
    },
});
